import { Button, Form } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import availableTimeApi from "../../api/availableTime";
import { formatAvailableTime, getFileIcon } from "../../common";
import { AvailableTime, AvailableTimeByDay, AvailableTimeEachUser, ProfileType, StaffProfile, StudentProfile } from "../../common/type";
import AvailableTimePicker from "../../components/AvailableTimePicker";
import EditField from "../../components/EditField";
import Field from "../../components/Field";
import InputDate from "../../components/InputDate";
import InputSelect from "../../components/InputSelect";
import Layout from "../../components/Layout";
import Timeline from "../../components/Timeline";
import Title from "../../components/Title";
import { BRANCHES, COURSES, events, STATUS, STATUS_STAFF, STATUS_STUDENT, STATUS_USER, USER_ROLES, UserRoleKey } from "../../constant";
import { AppDispatch, RootState } from "../../store";
import { updateProfileStaff, updateProfileStudent } from "../../store/slices/authSlice";
import { useGetAllBank } from "../../hooks/query/useGetAllBank";
import BankSelect from "../../components/BankSelect";

function Profile() {
    const dispatch = useDispatch<AppDispatch>();
    const [status, setStatus] = useState(false);
    const [data, setData] = useState<AvailableTimeByDay>({});
    const [dataAllUser, setDataAllUser] = useState<{
        teacher: AvailableTimeEachUser[],
        student: AvailableTimeEachUser[],
        studentManagement: AvailableTimeEachUser[],
    }>({
        teacher: [],
        student: [],
        studentManagement: [],
    });
    const { user } = useSelector((state: RootState) => state.auth);
    const bankInfo = user?.bankInfo;
    const isStudent = user?.roles?.includes("Học viên") || user?.roles?.includes("student");
    const isTeacher = user?.roles?.includes("Giáo viên") || user?.roles?.includes("teacher");
    const isStudentManagement = user?.roles?.includes("Quản sinh") || user?.roles?.includes("student_management");
    // const isBranchManagement = user?.roles?.includes("Quản lý chi nhánh") || user?.roles?.includes("branch_management");
    // const isStaff = isTeacher || isStudentManagement || isBranchManagement;
    const isAdmin = user?.roles?.includes("Admin") || user?.roles?.includes("admin ");
    const [view, setView] = useState(false);
    const [profile, setProfile] = useState<ProfileType & { courseName: string[] }>(({
        fullName: "",
        birthday: "",
        id: "",
        startDate: "",
        branchCode: "",
        courseCodes: [],
        status: STATUS_USER.inactive,
        role: [],
        teacherName: "",
        address: "",
        phoneNumber: "",
        currentGrade: 0,
        cvUrl: null,
        bankAccountNumber: "",
        bankId: 0, 
        email: "",
    } as unknown) as ProfileType & { courseName: string[] });

    useEffect(() => {
        if (!user) return;
        console.log("user profile:", user);
        const branchName = user?.branch?.code || "";
        const statusFromUser = user?.status;

        const isoDate = (date?: string | null) => {
            if (!date) return "";
            const d = new Date(date);
            if (isNaN(d.getTime())) return "";
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        if (isStudent) {
            setProfile(prev => ({
                ...prev,
                fullName: user?.fullName || "",
                birthday: isoDate(user?.dateOfBirth),
                id: user?.studentId ? String(user.studentId) : "",
                status: statusFromUser,
                startDate: isoDate(user?.startDate),
                branchName,
                courseName: (user?.courses || []).map((c: any) => c.code).filter(Boolean),
                teacherName: user?.classes?.[0]?.teacherName || "",
                currentGrade: user?.currentGrade || 0,
                role: user.roles || [],
                phoneNumber: user.phoneNumber || "",
                address: user.address || "",
                email: user.email || "",
            }));
        } else {
            setProfile(prev => ({
                ...prev,
                fullName: user?.fullName || "",
                birthday: isoDate(user?.dateOfBirth),
                id: user?.staffId ? String(user.staffId) : "",
                status: statusFromUser,
                startDate: isoDate(user?.startDate),
                branchName,
                courseName: (user?.teacher?.courses || []).map((c: any) => c.code).filter(Boolean),
                role: user?.roles || [],
                phoneNumber: user?.phoneNumber || "",
                address: user?.address || "",
                cvUrl: user?.cvUrl || null,
                bankAccountNumber: user?.bankInfo?.bankAccountNumber || "",
                bankId: user?.bankInfo?.id || 0,
                email: user?.email || "",
            }));
        }
    }, [user, isStudent]);

    const handleEditProfile = async () => {
        if (status) {
            try {
                if (isStudent) {
                    const formatDataStudent = {
                        fullName: profile.fullName,
                        dateOfBirth: profile.birthday,
                        schoolGrade: Number(profile.currentGrade),
                        status: profile.status,
                        startDate: profile.startDate,
                        courseCodes: profile.courseName, // array
                        teacherName: profile.teacherName,
                        branchCode: profile.branchName,
                        phoneNumber: profile.phoneNumber,
                        address: profile.address,
                        email: profile.email,
                    };
                    await dispatch(
                        updateProfileStudent({ id: Number((user as StudentProfile).studentId), data: formatDataStudent })
                    ).unwrap();
                    toast.success("Cập nhật thông tin thành công")
                } else {
                    const formatDataStaff = new FormData();

                    formatDataStaff.append("fullName", profile.fullName);
                    formatDataStaff.append("dateOfBirth", profile.birthday);
                    formatDataStaff.append("status", profile.status);
                    formatDataStaff.append("startDate", profile.startDate);
                    if (profile.courseCodes) {
                        profile.courseCodes.forEach((code: string) => {
                            formatDataStaff.append("courseCodes[]", code);
                        });
                    }
                    formatDataStaff.append("branchCode", profile.branchName);
                    formatDataStaff.append("phoneNumber", String(profile.phoneNumber));
                    formatDataStaff.append("address", String(profile.address));
                    formatDataStaff.append("bankAccountNumber", String(profile.bankAccountNumber));
                    formatDataStaff.append("bankId", String(profile.bankId));
                    formatDataStaff.append("email", String(profile.email));

                    if (profile.cvUrl instanceof File) {
                        formatDataStaff.append("file", profile.cvUrl as File);
                    }
                    console.log("formatDataStaff:", formatDataStaff);
                    await dispatch(
                        updateProfileStaff({ id: Number((user as StaffProfile).staffId), data: formatDataStaff })
                    ).unwrap();
                    toast.success("Cập nhật thông tin thành công")
                }
            } catch (error) {
                console.error(error);
            }
        }

        setStatus(prev => !prev);
    };

    const handleGetData = async (data: string, name: string) => {
        setProfile(prev => ({ ...prev, [name]: data }));
    };

    const handleAddCourse = (courseCode: string) => {
        if (!courseCode) return;
        setProfile(prev => {
            const list = Array.isArray(prev.courseName) ? prev.courseName : [];
            if (list.includes(courseCode)) return prev;
            return { ...prev, courseName: [...list, courseCode] };
        });
    };

    const handleRemoveCourse = (courseCode: string) => {
        setProfile(prev => ({
            ...prev,
            courseName: (prev.courseName || []).filter((c: string) => c !== courseCode),
        }));
    };

    const branchOptions = Object.entries(BRANCHES).map(([value, label]) => ({ value, label }));
    const courseOptions = Object.entries(COURSES).map(([value, label]) => ({ value, label }));
    const [form] = Form.useForm();

    const handleGetAvailableTimes = async (availableTimes: any[]) => {
        try {
            const res = await availableTimeApi.addAvailableTime({ availableTimes });
            if (res.statusCode === STATUS.SUCCESS || res.statusCode === 201) {
                fetchAvailableTimes();
                toast.success("Cập nhật thời gian biểu thành công");
            }
        } catch (error: any) {
            if (error.message === 'Request failed with status code 400') {
                toast.error("Thời gian biểu bị trùng, vui lòng kiểm tra lại");
                return;
            }
            toast.error("Cập nhật thời gian biểu thất bại");
        }
    }
    const fetchAvailableTimes = async () => {
        const res = await availableTimeApi.getAvailableTime();

        const availableTimes = res.data as unknown as AvailableTime[];

        const formatTime: AvailableTime[] = availableTimes.map((item: AvailableTime) => ({
            ...item,
            startTime: dayjs(item.startTime, 'HH:mm:ss').format('HH:mm'),
            endTime: dayjs(item.endTime, 'HH:mm:ss').format('HH:mm'),
        }));
        const groupedTimes = formatAvailableTime(formatTime);

        setData(groupedTimes);
    };
    useEffect(() => {
        fetchAvailableTimes();
    }, []);

    const fortmatAvailableTimeOfUser = (data: AvailableTimeEachUser[]) => {
        const teacherData: AvailableTimeEachUser[] = [];
        const studentData: AvailableTimeEachUser[] = [];
        const studentManagementData: AvailableTimeEachUser[] = [];
        for (let i = 0; i < data.length; i++) {
            const formatData = {
                ...data[i],
                availableTime: data[i]?.availableTime?.map((item: AvailableTime) => ({
                    ...item,
                    startTime: dayjs(item.startTime, 'HH:mm:ss').format('HH:mm'),
                    endTime: dayjs(item.endTime, 'HH:mm:ss').format('HH:mm'),
                })),
            };
            if (formatData.roles.includes('teacher')) {
                teacherData.push(formatData);
            } else if (formatData.roles.includes('student')) {
                studentData.push(formatData);
            } else if (formatData.roles.includes('student_management')) {
                studentManagementData.push(formatData);
            }
        }
        setDataAllUser({
            teacher: teacherData,
            student: studentData,
            studentManagement: studentManagementData
        });
    }

    const fetchAvailableTimeOfUser = async () => {
        const res = await availableTimeApi.getAvailableTimeOfUser();
        const result = res.data as unknown as AvailableTimeEachUser[]
        fortmatAvailableTimeOfUser(result);
    }

    const handleChangeView = async () => {
        setView(!view);
        if (!view) {
            await fetchAvailableTimeOfUser();
        }
    }

    const handleUploadCV = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProfile(prev => ({
            ...prev,
            cvUrl: file,
        }));
    };

    const getFileName = (file: string | File) => {
        if (typeof file === "string") {
            try {
                const pathname = new URL(file).pathname;
                const rawName = pathname.split("/").pop() || "";
                return rawName.replace(/^\d+-/, "");
            } catch {
                return "CV";
            }
        }
        return file.name;
    };

    const { data: listBanks } = useGetAllBank();
    const banksData = useMemo(() => {
        if (!listBanks?.data) return [];
        return listBanks.data.map((bank: any) => ({
            id: bank.id,
            code: bank.code,
            shortName: bank.shortName,
            name: bank.name,
            logoUrl: bank.logoUrl,
        }));
    }, [listBanks]);

    return (
        <Layout>
            <section className="py-[60px] px-[20px] bg-light">
                <Title
                    title={isStudent ? "Thông Tin Học Sinh" : "Thông Tin Nhân Viên"}
                    subTitle={isStudent ? "Quản lý toàn bộ quá trình học tập của học sinh" : "Quản lý thông tin của nhân viên"}
                />
                 <p className="text-center uppercase font-bold text-[30px] text-primary mt-[10px]">{USER_ROLES[user.roles[0] as UserRoleKey]}</p>
                <div className="bg-white rounded-box shadow-default p-[25px] mt-[60px]">
                    <div className="border-b border-under flex justify-between pb-[20px] mb-[10px]">
                        <h2 className="text-bold font-bold text-[24px]">Thông tin cá nhân</h2>
                        <p className="bg-primary text-white rounded-large py-[8px] px-[15px] font-[600] h-[62px] lg:h-fit flex justify-center items-center">
                            {isStudent ? "Mã HS" : "Mã NV"}:{" "}
                            {profile.id}
                        </p>
                    </div>
                    {status ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:min-h-[222px]">
                            <EditField
                                title="Họ và tên"
                                value={profile.fullName || ""}
                                name="fullName"
                                onData={handleGetData}
                            />
                            <InputDate
                                label="Ngày sinh"
                                name="birthday"
                                value={profile.birthday}
                                onChange={(e) => {
                                    setProfile(prev => ({ ...prev, birthday: e.target.value }));
                                    handleGetData(e.target.value, "birthday");
                                }}
                            />
                            {isStudent && (
                                <EditField
                                    title="Lớp hiện tại"
                                    value={String(profile.currentGrade || 0)}
                                    name="currentGrade"
                                    onData={handleGetData}
                                />
                            )}
                            <div className="flex flex-col w-full items-start">
                                <label className="font-medium mb-[4px]">Trạng thái</label>
                                <select
                                    disabled={!isAdmin}
                                    className={`${isAdmin ? 'opacity-100' : 'opacity-45'} outline-none border border-[#e0e0e0] rounded-[4px] py-[4px] px-[8px] w-full h-[46px] focus:border-[#3498db] focus:ring-1 focus:ring-[#3498db] transition-all duration-200`}
                                    value={profile.status || ""}
                                    onChange={(e) => setProfile({ ...profile, status: e.target.value })}
                                >
                                    {isStudent ? (
                                        <>
                                            <option value={STATUS_USER.active}>Đang học</option>
                                            <option value={STATUS_USER.pending}>Đang chờ</option>
                                            <option value={STATUS_USER.inactive}>Nghỉ học</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value={STATUS_USER.active}>Đang làm</option>
                                            <option value={STATUS_USER.pending}>Đang chờ</option>
                                            <option value={STATUS_USER.inactive}>Đã nghỉ</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <InputDate
                                label="Ngày đăng ký"
                                name="startDate"
                                value={profile.startDate}
                                onChange={(e) => setProfile(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                            {!isStudentManagement && <div className="flex flex-col w-full">
                                <label className="font-medium mb-[4px]">Khóa học hiện tại</label>
                                <div className="flex items-center gap-2">
                                    <InputSelect
                                        label=""
                                        name="courseName"
                                        data={courseOptions}
                                        isDisabled={isStudentManagement}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            console.log("val:", e.target.value);
                                            if (val) handleAddCourse(val);
                                        }}
                                    />
                                </div>
                                {/* hiển thị các thẻ khóa học khi edit */}
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {(profile.courseName || []).map((c) => (
                                        <span
                                            key={c}
                                            className="py-[6px] px-[10px] text-[13px] font-[600] rounded-[15px] flex items-center gap-2 bg-blue-100 text-blue-600"
                                        >
                                            <span>{COURSES[c as keyof typeof COURSES] || c}</span>
                                            <i
                                                className="fa fa-times cursor-pointer"
                                                onClick={() => handleRemoveCourse(c)}
                                                title="Xóa"
                                            />
                                        </span>
                                    ))}
                                </div>
                            </div>
                            }
                            {isStudent && (
                                <EditField
                                    title="Giáo viên chủ nhiệm"
                                    value={profile.teacherName || ""}
                                    name="teacherName"
                                    onData={handleGetData}
                                    isDisabled={true}
                                />
                            )}
                            <InputSelect
                                label={isStudent ? "Cơ sở học" : "Cơ sở làm việc"}
                                name="branchName"
                                data={branchOptions}
                                value={profile.branchName}
                                isDisabled={isStudent}
                                onChange={(e) => setProfile(prev => ({ ...prev, branchName: e.target.value }))}
                            />
                            <EditField
                                title="Số diện thoại"
                                value={profile.phoneNumber || ""}
                                name="phoneNumber"
                                onData={handleGetData}
                            />
                            <EditField
                                title="Địa chỉ"
                                value={profile.address || ""}
                                name="address"
                                onData={handleGetData}
                            />
                            {!isStudent && <div className="flex flex-col w-full items-start">
                                <label className="font-medium mb-[6px]">CV</label>

                                <div className="flex flex-col gap-2 w-full">
                                    {profile.cvUrl && (
                                        <div
                                            className={`cursor-pointer flex items-center gap-3 border rounded-lg px-4 py-3 w-full
                                        ${profile.cvUrl instanceof File ? "bg-blue-50" : "bg-gray-50"}
                                    `}
                                        >
                                            {getFileIcon(getFileName(profile.cvUrl))}
                                            <span className="text-sm font-medium truncate">
                                                {getFileName(profile.cvUrl)}
                                            </span>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        id="cv-upload"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleUploadCV}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="cv-upload"
                                        className="cursor-pointer text-blue-600 font-medium hover:underline"
                                    >
                                        {profile.cvUrl ? "Thay đổi CV" : "Upload CV"}
                                    </label>
                                </div>
                            </div>
                            }
                            {!isStudent && (
                                <>
                                    <EditField
                                        title="STK Ngân Hàng"
                                        value={profile.bankAccountNumber || ""}
                                        name="bankAccountNumber"
                                        onData={handleGetData}
                                    />

                                    <BankSelect
                                        label="Tên Ngân Hàng"
                                        value={profile.bankId || 0}  // Truyền id
                                        banks={banksData}
                                        onChange={(bankId) => {  // Nhận id từ BankSelect
                                            setProfile(prev => ({ ...prev, bankId: bankId }));
                                            handleGetData(String(bankId), "bankId");
                                        }}
                                    />
                                </>
                            )}
                            <Field title="Email" value={profile.email || "Chưa có thông tin"} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <Field title="Họ và tên" value={profile.fullName || ""} />
                            <Field title="Ngày sinh" value={profile.birthday || ""} />
                            {(profile.role?.includes("Học viên") || profile.role?.includes("student")) && (
                                <Field
                                    title="Lớp hiện tại"
                                    value={profile.currentGrade || 0}
                                />
                            )}
                            <div className="w-full h-fit mt-[30px]">
                                <label className="text-title text-small">Trạng thái</label>
                                <p
                                    className={`py-[5px] px-[10px] text-[13px] font-[600] rounded-[15px] w-fit mt-[10px] ${profile.status === STATUS_USER.active
                                        ? "bg-bg_success text-success"
                                        : profile.status === STATUS_USER.pending ?
                                            "bg-yellow-100 text-yellow-600"
                                            : "bg-red-300 text-red-700"
                                        }`}
                                >
                                    {isStudent
                                        ? STATUS_STUDENT[profile.status as keyof typeof STATUS_STUDENT]
                                        : STATUS_STAFF[profile.status as keyof typeof STATUS_STAFF] || ""}
                                </p>
                            </div>
                            <Field title="Ngày đăng ký" value={profile.startDate || "Chưa có thông tin"} />
                            {!isStudentManagement && <div className="mt-[30px]">
                                <label className="text-title text-small">Khóa học hiện tại</label>
                                <div className="flex flex-wrap gap-2 mt-[10px]">
                                    {(profile.courseName || []).length === 0 ? (
                                        <p className="text-small text-gray-500">Chưa có khóa học</p>
                                    ) : (
                                        (profile.courseName || []).map((c) => (
                                            <span
                                                key={c}
                                                className={`py-[5px] px-[10px] text-[13px] font-[600] rounded-[15px] w-fit mt-[0px] bg-blue-100 text-blue-600`}
                                            >
                                                {COURSES[c as keyof typeof COURSES] || c}
                                            </span>
                                        ))
                                    )}
                                </div>
                            </div>}
                            {isStudent && (
                                <Field title="Giáo viên chủ nhiệm" value={profile.teacherName || ""} />
                            )}
                            <Field
                                title={isStudent ? "Cơ sở học" : "Cơ sở làm việc"}
                                value={BRANCHES[profile.branchName as keyof typeof BRANCHES] || "Chưa có thông tin"}
                            />
                            <Field title="Số diện thoại" value={profile.phoneNumber || "Chưa có thông tin"} />
                            <Field title="Địa chỉ" value={profile.address || "Chưa có thông tin"} />
                            {!isStudent && (
                                <div className="w-full h-fit mt-[30px]">
                                    <label className="text-title text-small">CV</label>
                                    {typeof profile.cvUrl === "string" && profile.cvUrl ? (
                                        <a
                                            href={profile.cvUrl}
                                            download
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 border rounded-lg px-4 py-3 bg-gray-50 w-full hover:bg-gray-100 transition"
                                        >
                                            {getFileIcon(getFileName(profile.cvUrl))}
                                            <span className="text-sm font-medium truncate">
                                                {getFileName(profile.cvUrl)}
                                            </span>
                                        </a>
                                    ) : (
                                        <p className="text-bold text-mid font-medium mt-[8px]">
                                            Chưa có file CV
                                        </p>
                                    )}
                                </div>
                            )}
                            {!isStudent && (
                                <>
                                    <Field
                                        title="STK ngân hàng"
                                        value={bankInfo?.bankAccountNumber || "Chưa có thông tin"}
                                    />
                                    <div className="w-full h-fit mt-[30px]">
                                        <div className="w-full h-fit">
                                            <label className="text-title text-small">Ngân hàng</label>
                                            {bankInfo ? (
                                                <div className="flex items-center gap-3 mt-[8px]">
                                                    <img
                                                        src={bankInfo.logoUrl}
                                                        alt={bankInfo.shortName}
                                                        className="w-7 h-7 object-contain"
                                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                                    />
                                                    <div>
                                                        <p className="text-bold text-mid font-medium">
                                                            {bankInfo.shortName}
                                                        </p>
                                                        <p className="text-[13px] text-gray-500">
                                                            {bankInfo.name}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-bold text-mid font-medium mt-[8px]">
                                                    Chưa có thông tin
                                                </p>
                                            )}
                                        </div>
                                    </div></>
                            )}
                            <Field title="Email" value={profile.email || "Chưa có thông tin"} />
                        </div>
                    )}
                    <div
                        onClick={handleEditProfile}
                        className="mt-[40px] lg:mt-[40px] flex lg:justify-end items-center text-[16px] text-primary opacity-80 hover:opacity-100 cursor-pointer"
                    >
                        <p className="mr-[10px] font-bold">
                            {status ? "Lưu thông tin" : "Chỉnh sửa thông tin"}
                        </p>
                        {status ? <i className="fa fa-check"></i> : <i className="fa fa-edit"></i>}
                    </div>
                </div>
                <div className="bg-white rounded-box shadow-default p-[25px] mt-[60px]">
                    <h2 className="text-bold font-bold text-[24px] text-center">Thời gian biểu cá nhân</h2>
                    <Form form={form}>
                        <AvailableTimePicker onGetAvailableTimes={handleGetAvailableTimes} />
                    </Form>
                    {isStudent || isTeacher ? (
                        <table className="w-full mx-auto border border-collapse border-[#e0e0e0] mt-[40px]">
                            <thead>
                                <tr className="h-[40px]">
                                    <th className="border">Họ tên</th>
                                    <th className="border w-[10%]">Vai trò</th>
                                    <th className="border w-[10%]">Thứ 2</th>
                                    <th className="border w-[10%]">Thứ 3</th>
                                    <th className="border w-[10%]">Thứ 4</th>
                                    <th className="border w-[10%]">Thứ 5</th>
                                    <th className="border w-[10%]">Thứ 6</th>
                                    <th className="border w-[10%]">Thứ 7</th>
                                    <th className="border w-[10%]">Chủ Nhật</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="h-[40px]">
                                    <td className="border text-center align-middle">{user.fullName}</td>
                                    <td className="border text-center align-middle">{USER_ROLES[user.roles[0] as UserRoleKey]}</td>
                                    <td className="border text-center align-middle">{data?.monday?.length > 0 && data?.monday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">{data?.tuesday?.length > 0 && data?.tuesday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">{data?.wednesday?.length > 0 && data?.wednesday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">{data?.thursday?.length > 0 && data?.thursday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">{data?.friday?.length > 0 && data?.friday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">
                                        {data?.saturday?.length > 0 && data?.saturday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                    <td className="border text-center align-middle">{data?.sunday?.length > 0 && data?.sunday.map((item) => (
                                        <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        <div>
                            <table className="w-full mx-auto border border-collapse border-[#e0e0e0] mt-[40px]">
                                <thead>
                                    <tr className="h-[40px]">
                                        <th className="border">Họ tên</th>
                                        <th className="border w-[10%]">Vai trò</th>
                                        <th className="border w-[10%]">Thứ 2</th>
                                        <th className="border w-[10%]">Thứ 3</th>
                                        <th className="border w-[10%]">Thứ 4</th>
                                        <th className="border w-[10%]">Thứ 5</th>
                                        <th className="border w-[10%]">Thứ 6</th>
                                        <th className="border w-[10%]">Thứ 7</th>
                                        <th className="border w-[10%]">Chủ Nhật</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="h-[40px]">
                                        <td className="border text-center align-middle">{user.fullName}</td>
                                        <td className="border text-center align-middle">{USER_ROLES[user.roles[0] as UserRoleKey]}</td>
                                        <td className="border text-center align-middle">{data?.monday?.length > 0 && data?.monday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">{data?.tuesday?.length > 0 && data?.tuesday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">{data?.wednesday?.length > 0 && data?.wednesday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">{data?.thursday?.length > 0 && data?.thursday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">{data?.friday?.length > 0 && data?.friday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">
                                            {data?.saturday?.length > 0 && data?.saturday.map((item) => (
                                                <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                        <td className="border text-center align-middle">{data?.sunday?.length > 0 && data?.sunday.map((item) => (
                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="mt-[60px] mb-[20px]">
                                {view ? <Button onClick={handleChangeView} className="">Ẩn lịch</Button>
                                    : <Button onClick={handleChangeView} className="">Xem lịch trung tâm</Button>}
                            </div>
                            {
                                view && (<div className="overflow-x-auto">
                                    <h2 className="text-bold font-bold text-[24px] text-center">Thời gian biểu thành viên của trung tâm</h2>
                                    <table className="w-full mx-auto border border-collapse border-[#e0e0e0] mt-[20px]">
                                        <thead>
                                            <tr className="h-[40px]">
                                                <th className="border">Họ tên</th>
                                                <th className="border w-[10%]">Vai trò</th>
                                                <th className="border w-[10%]">Thứ 2</th>
                                                <th className="border w-[10%]">Thứ 3</th>
                                                <th className="border w-[10%]">Thứ 4</th>
                                                <th className="border w-[10%]">Thứ 5</th>
                                                <th className="border w-[10%]">Thứ 6</th>
                                                <th className="border w-[10%]">Thứ 7</th>
                                                <th className="border w-[10%]">Chủ Nhật</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                dataAllUser.teacher.map((user) => (
                                                    <tr className="h-[40px]" key={user.userId}>
                                                        <td className="border text-center align-middle">{user.name}</td>
                                                        <td className="border text-center align-middle">{USER_ROLES[user.roles[0] as UserRoleKey]}</td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'monday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'tuesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'wednesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'thursday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'friday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'saturday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'sunday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[green] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                    </tr>

                                                ))}
                                            {
                                                dataAllUser.student.map((user) => (
                                                    <tr className="h-[40px]" key={user.userId}>
                                                        <td className="border text-center align-middle">{user.name}</td>
                                                        <td className="border text-center align-middle">{USER_ROLES[user.roles[0] as UserRoleKey]}</td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'monday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'tuesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'wednesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'thursday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'friday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'saturday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'sunday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-secondary text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                    </tr>

                                                ))}
                                            {
                                                dataAllUser.studentManagement.map((user) => (
                                                    <tr className="h-[40px]" key={user.userId}>
                                                        <td className="border text-center align-middle">{user?.name}</td>
                                                        <td className="border text-center align-middle">{USER_ROLES[user.roles[0] as UserRoleKey]}</td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'monday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'tuesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'wednesday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user.availableTime?.filter(item => item.dayOfWeek === 'thursday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'friday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'saturday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                        <td className="border text-center align-middle">{user?.availableTime?.filter(item => item.dayOfWeek === 'sunday').map((item) => (
                                                            <span key={item.id} className="mx-auto my-[4px] w-[80%] border-none bg-[red] text-white flex justify-center items-center rounded-[10px]">{item.startTime} - {item.endTime}</span>))}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                                )}
                        </div>
                    )}
                </div>
                {isStudent && <div className="bg-white rounded-box shadow-default p-[25px] mt-[60px]">
                    <h2 className="text-bold font-bold text-[24px]">Lịch sử học tập</h2>
                    <div className="flex flex-col items-center p-6 h-fit">
                        <div className="lg:w-full relative border-l-[2px] border-secondary">
                            {events.map((event) => (
                                <Timeline event={event} index={`key-${event?.title}`} key={event?.title} />
                            ))}
                        </div>
                    </div>
                </div>}
            </section>
        </Layout>
    );
}

export default Profile;
